import { useEffect, useState, type ChangeEvent } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { Box, Container, Grid, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import UserProfileHeader from './components/UserProfileHeader';
import StatCard from './components/StatCard';
import LifetimeRecordsSection from './components/LifetimeRecordsSection';
import { userDashboardColors } from './theme';
import TableAreaOrderAdmin from '../../components/TableAreaOrderAdmin/TableAreaOrderAdmin';
import SearchInput from '../../components/SearchInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { changeFilter } from '../../store/order/orderSlice';
import useDebounce from '../../utils/useDebounce';
import { adminAPI } from '../../api/services/userApi';
import type { UserDashboardDataType } from '../../types/userDashboardTypes';
import { mapUserDashboardApiToView } from './mapUserDashboardData';

const metricIcons: Record<string, ReactNode> = {
  balance: <AccountBalanceWalletIcon />,
  subscription: <WorkspacePremiumIcon />,
  price: <LocalOfferIcon />,
  revenue: <AttachMoneyIcon />,
};

const UserDashboardPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const dispatch = useAppDispatch();
  const orderFilter = useAppSelector((state) => state.orderSlice.filter);
  const [search, setSearch] = useState(orderFilter.findString);
  const [dashboardData, setDashboardData] = useState<UserDashboardDataType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentSearch = useDebounce(search);

  useEffect(() => {
    dispatch(changeFilter({ title: 'findString', value: currentSearch }));
  }, [currentSearch, dispatch]);

  useEffect(() => {
    if (!userId) {
      setError('User not found');
      setIsLoading(false);
      return;
    }

    const fetchDashboard = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await adminAPI.getUserDashboardStats(userId);
        console.warn('res', res);
        if (res.status === StatusCodes.OK && res.data.data) {
          setDashboardData(mapUserDashboardApiToView(res.data.data));
        } else {
          setError('Failed to load user dashboard');
        }
      } catch (err) {
        console.error(err);
        setError(typeof err === 'string' ? err : 'Failed to load user dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [userId]);

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, width: '100%', minHeight: '100vh', py: 3, px: { xs: 2, md: 4 } }}>
        <div className="styled-app__loader" />
      </Box>
    );
  }

  if (error || !dashboardData) {
    return (
      <Box sx={{ flex: 1, width: '100%', minHeight: '100vh', py: 3, px: { xs: 2, md: 4 } }}>
        <Typography color="error">{error ?? 'Failed to load user dashboard'}</Typography>
      </Box>
    );
  }

  const { profile, metrics, lifetimeRecords } = dashboardData;

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        minHeight: '100vh',
        py: 3,
        px: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg" disableGutters>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 3,
            border: `1px solid ${userDashboardColors.neutral.border}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
            p: { xs: 2.5, md: 3.5 },
          }}
        >
          <ArrowBackIcon onClick={() => navigate('/users')} sx={{ fontSize: 24, cursor: 'pointer' }} />

          <UserProfileHeader profile={profile} />

          <Grid container spacing={2}>
            {metrics.map((metric) => (
              <Grid item xs={12} sm={6} lg={3} key={metric.id}>
                <StatCard
                  icon={metricIcons[metric.id]}
                  label={metric.label}
                  value={metric.value}
                  subtext={metric.subtext}
                  variant={metric.variant}
                  subtextAccent={metric.id === 'price'}
                  coloredValue={metric.id === 'balance' || metric.id === 'revenue'}
                />
              </Grid>
            ))}
          </Grid>

          <LifetimeRecordsSection records={lifetimeRecords} />
        </Box>

        <Box
          sx={{
            mt: 3,
            bgcolor: '#fff',
            borderRadius: 3,
            border: `1px solid ${userDashboardColors.neutral.border}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
            p: { xs: 2.5, md: 3.5 },
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: userDashboardColors.neutral.heading,
              mb: 2.5,
              textTransform: 'uppercase',
            }}
          >
            Orders
          </Typography>
          <Box sx={{ mb: 3, maxWidth: { xs: '100%', lg: '50%' } }}>
            <SearchInput
              onChange={handleChangeSearch}
              value={search}
              nameInput="user-dashboard-order-search"
              placeHolder="Search"
            />
          </Box>
          <TableAreaOrderAdmin userId={userId} />
        </Box>
      </Container>
    </Box>
  );
};

export default UserDashboardPage;
